/* eslint-disable */
import React, { useState, useEffect } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericTabs, { TabOption } from "@ui-components/Tabs/GenericTabs";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSearchableDropdown from "@ui-components/Dropdown/GenericSearchableDropdown";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericInlineAlert from "@ui-components/Alert/GenericInlineAlert";
import { useAppSelector } from "@redux/hooks";
import { SkeletonBlock, SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import PropagationTabContent from "./PropagationTabContent";
import { getBgpNeighborsDropdown } from "../../request/request";
import {
  createInboundPropagationRuleCluster,
  createOutboundPropagationRuleCluster,
  updateInboundPropagationRuleCluster,
  updateOutboundPropagationRulesCluster,
  buildInboundPropagationRuleClusterPayload,
  buildOutboundPropagationRulesClusterPayload,
  deleteInboundPropagationRuleCluster,
  deleteOutboundPropagationRuleCluster,
} from "../../Cluster/request/request";
import {
  createInboundPropagationRule,
  createOutboundPropagationRule,
  updateOutboundPropagationRules,
  buildInboundPropagationRulePayload,
  buildOutboundPropagationRulesPayload,
  getPropagationRuleById,
  deleteInboundPropagationRule,
  deleteOutboundPropagationRule,
} from "../../request/request";
import {
  getBgpNeighborIds,
  getCurrentRuleKey,
  hasMeaningfulInboundFormData as hasMeaningfulInboundFormDataHelper,
  hasMeaningfulOutboundFormData as hasMeaningfulOutboundFormDataHelper,
  transformRuleDetailsResponse,
  defaultOutboundRuleForm,
  normalizeOutboundFormData,
  hasDynamicEniResolverNextHop,
  validateInboundDynamicTags,
  validateInboundNexthopRetry,
  validateNexthopVerificationValues,
  validateOutboundRules,
  hasMeaningfulOutboundRule,
  withoutInboundNexthopRetry,
  withoutNexthopVerification,
} from "../../../../helpers/cloudPropagationHelpers";

const TABS: TabOption[] = [
  { label: "Inbound", value: "inbound" },
  { label: "Outbound", value: "outbound" },
];

interface CPModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: any) => void | Promise<void>;
  mode?: "add" | "edit";
  initialValues?: any;
  title?: React.ReactNode;
  badge?: React.ReactNode;
  tag?: string;
  bgpNeighborId?: number;
  reflectorId?: number; // Add reflectorId prop
  clusterId?: number; // Add clusterId for cluster mode
  isSubmitting?: boolean;
  getAccessTokenSilently?: () => Promise<string>; // Add token function for cluster mode
  hasExistingConfig?: boolean; // Add flag to indicate if config exists
  inboundId?: number | null; // Add inboundId for cluster mode updates
  outboundId?: number | null; // Add outboundId for cluster mode updates
  neighborAddress?: string; // Add neighborAddress prop for cluster mode
  propagationRuleId?: number | string; // Rule ID (numeric or UUID) for fetch/update/delete
  // True when the associated reflector (standalone) or any instance in the cluster (cluster mode)
  // is below DYNAMIC_TAG_MIN_VERSION — disables the dynamic-tag-based options (CTX-163).
  disableDynamicTagOptions?: boolean;
  // Same, for the inbound "Advanced settings → Retry" block, gated on its own
  // minimum (NEXTHOP_RETRY_MIN_VERSION) — the settings are also stripped from
  // the payload so an unsupported reflector never receives them (CTX-181).
  disableNexthopRetry?: boolean;
  // ≥ NEXTHOP_WATCH_MIN_VERSION: the Retry block gives way to the reflector-wide
  // "Next-hop verification" block (CTX-347).
  useNexthopVerification?: boolean;
}

const defaultTabState = {
  ruleName: "",
  policy: "",
  policySelection: "specific", // Changed from "dynamic" since it's now disabled
  routingTableTagName: "",
  routingTableTagValue: "",
  nextHopSelection: "specific", // Changed from "dynamic" since it's now disabled
  nextHopTagName: "",
  nextHopTagValue: "",
  routingTables: [], // Add routing tables array
  selectedVpc: "", // Add VPC selection
  persistence: true, // Default: hold route on peer down
};

const defaultOutboundTabState = {
  rules: [defaultOutboundRuleForm()],
};

// The backend re-validates every rule and answers with a message naming the offending
// field (e.g. an out-of-range retry cadence). Surface it in the modal's inline alert,
// next to the form, instead of only logging it (CTX-181).
const getSaveErrorMessage = (error: unknown): string => {
  const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return message?.trim() || "Failed to save propagation rules. Please try again.";
};

const CPModal: React.FC<CPModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode = "add",
  initialValues = {},
  title = (
    <span className="flex items-center gap-2 font-semibold">
      Configure propagation rule
    </span>
  ),
  badge,
  tag,
  bgpNeighborId,
  reflectorId,
  clusterId,
  isSubmitting = false,
  getAccessTokenSilently,
  hasExistingConfig = false,
  inboundId,
  outboundId,
  neighborAddress,
  propagationRuleId,
  disableDynamicTagOptions,
  disableNexthopRetry = false,
  useNexthopVerification = false,
}) => {
  const { tenantId } = useAppSelector((state: any) => state.selection);
  const [activeTab, setActiveTab] = useState<string>("inbound");
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);
  const [ruleName, setRuleName] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");

  // Mode selection state (only for create mode)
  // In edit mode, use the tag prop; in add mode, allow user to select
  const [selectedMode, setSelectedMode] = useState<"Enterprise" | "Telco">("Enterprise");
  
  // Determine if Telco mode: use selectedMode in add mode, tag prop in edit mode
  const isTelco = mode === "add" 
    ? selectedMode === "Telco" 
    : tag === "Telco";
  
  // BGP Neighbors state
  const [selectedBgpNeighbors, setSelectedBgpNeighbors] = useState<Array<{ id: number; address: string; label: string }>>([]);
  const [bgpNeighborOptions, setBgpNeighborOptions] = useState<Array<{ value: string; label: string; neighborAddress?: string; id?: number }>>([]);
  const [selectedBgpNeighbor, setSelectedBgpNeighbor] = useState<string>("");
  const [isLoadingBgpNeighbors, setIsLoadingBgpNeighbors] = useState(false);
  
  // State for loading rule details in edit mode
  const [isLoadingRuleDetails, setIsLoadingRuleDetails] = useState(false);
  const [fetchedRuleDetails, setFetchedRuleDetails] = useState<any>(null);
  const [ruleIds, setRuleIds] = useState<{
    propagationRuleId?: number | string;
    inboundId?: number;
    outboundId?: number
  }>({});
  // Set when the user deletes every VRF (inbound) or every outbound rule
  // during this session, so hasExisting*Data can't fall back to the stale
  // fetch-time snapshot and treat the rule as still having persisted data.
  const [inboundFullyDeleted, setInboundFullyDeleted] = useState(false);
  const [outboundFullyDeleted, setOutboundFullyDeleted] = useState(false);
  // VRFs/outbound rules the user removed inside the modal during this session.
  // The actual DELETE call is deferred until "Save" is pressed (see handleSave),
  // so Cancel/close never touches the backend (CTX-154).
  const [pendingInboundDeleteIds, setPendingInboundDeleteIds] = useState<number[]>([]);
  const [pendingOutboundDeleteIds, setPendingOutboundDeleteIds] = useState<number[]>([]);
  
  // Helper function to get default state based on mode
  // Use selectedMode in add mode, tag prop in edit mode
  // Can optionally take a mode parameter to get state for a specific mode
  const getDefaultInboundState = React.useCallback((overrideMode?: "Enterprise" | "Telco") => {
    const modeToUse = overrideMode !== undefined 
      ? overrideMode 
      : (mode === "add" ? selectedMode : (tag === "Telco" ? "Telco" : "Enterprise"));
    
    const currentIsTelco = modeToUse === "Telco";
      
    if (currentIsTelco) {
      // For Telco mode, include a default VRF
      return {
        ...defaultTabState,
        vrfs: [{
          name: "Default VRF",
          routeTargets: [],
          newRouteTarget: "",
          routingTable: "",
          nextHopEni: "",
          policySelection: "specific", // Changed from "dynamic" since it's now disabled
          nextHopSelection: "specific", // Changed from "dynamic" since it's now disabled
          routingTables: [],
          enis: [],
          expanded: true,
          persistence: true, // Default: hold route on peer down
        }]
      };
    }
    return { ...defaultTabState };
  }, [mode, selectedMode, tag]);
  
  // Initialize state with defaults - will be updated by useEffect when modal opens with initialValues
  // Don't use initialValues in useState initializer as they may change when switching between rules
  // Use 'any' type to allow for dynamic properties (vrfs, enis, routeTargets) that vary by mode
  const [inbound, setInbound] = useState<any>(() => {
    // Always start with defaults - the useEffect will apply initialValues when modal opens
    // Note: We can't use isTelco here as it's not available yet, so we'll use defaultTabState
    // The useEffect will properly set telco defaults when needed
    return { ...defaultTabState };
  });
  
  const [outbound, setOutbound] = useState<any>(() => {
    // Always start with defaults - the useEffect will apply initialValues when modal opens
    return { ...defaultOutboundTabState };
  });

  // Detect if we have existing data for each tab (use fetchedRuleDetails when present so edit mode uses PUT not POST)
  const sourceForExisting = fetchedRuleDetails || initialValues;
  const hasExistingInboundData = React.useMemo(() => {
    if (inboundFullyDeleted) return false;
    if (ruleIds.inboundId != null) return true;
    if (!sourceForExisting?.inbound) return false;
    const inboundData = sourceForExisting.inbound;
    if (inboundData.vrfs && inboundData.vrfs.length > 0) {
      const vrf = inboundData.vrfs[0];
      return vrf.routingTables?.length > 0 || vrf.enis?.length > 0 || vrf.routeTargets?.length > 0 ||
             vrf.routingTableTagName || vrf.routingTableTagValue || vrf.nextHopTagName || vrf.nextHopTagValue;
    }
    return inboundData.routingTables?.length > 0 || inboundData.enis?.length > 0 ||
           inboundData.routingTableTagName || inboundData.routingTableTagValue ||
           inboundData.nextHopTagName || inboundData.nextHopTagValue;
  }, [sourceForExisting?.inbound, ruleIds.inboundId, inboundFullyDeleted]);

  const hasExistingOutboundData = React.useMemo(() => {
    if (outboundFullyDeleted) return false;
    const normalized = normalizeOutboundFormData(sourceForExisting?.outbound);
    if (normalized.rules.some((r) => r.outboundId != null)) return true;
    return hasMeaningfulOutboundFormDataHelper(sourceForExisting?.outbound);
  }, [sourceForExisting?.outbound, outboundFullyDeleted]);

  // Wizard flow helpers:
  // Save behavior: on either tab, we submit BOTH inbound + outbound.
  // If the "other" tab has no meaningful edits, we fall back to initialValues to avoid overwriting.
  const hasMeaningfulInboundFormData = React.useCallback((): boolean => {
    return hasMeaningfulInboundFormDataHelper(inbound, isTelco);
  }, [inbound, isTelco]);

  const hasMeaningfulOutboundFormData = React.useCallback((): boolean => {
    return hasMeaningfulOutboundFormDataHelper(outbound);
  }, [outbound]);

  // Track which rule we're currently editing to detect when switching between different rules
  // Use a combination of bgpNeighborId and neighborAddress to uniquely identify a rule
  const currentRuleKeyRef = React.useRef<string | null>(null);
  // Once hydrated for a rule key, do not re-apply fetchedRuleDetails (stale) on parent re-renders
  const formHydratedForRuleKeyRef = React.useRef<string | null>(null);
  
  // Generate a unique key for the current rule being edited
  const getCurrentRuleKeyMemoized = React.useCallback(() => {
    return getCurrentRuleKey(bgpNeighborId, neighborAddress, clusterId);
  }, [bgpNeighborId, neighborAddress, clusterId]);
  
  // Helper function to extract BGP neighbor IDs from selected neighbors or prop
  const getBgpNeighborIdsMemoized = React.useCallback((): number[] => {
    return getBgpNeighborIds(selectedBgpNeighbors, bgpNeighborId);
  }, [selectedBgpNeighbors, bgpNeighborId]);

  const buildOutboundSubmitOp = React.useCallback(
    (
      outboundForm: any,
      opts: {
        isCluster: boolean;
        isTelcoMode: boolean;
        modeToSend?: string;
        bgpNeighborIds: number[];
        ruleIdStr?: string;
        isUpdate: boolean;
      }
    ) => {
      const normalized = normalizeOutboundFormData(outboundForm);
      const rulesToSubmit = normalized.rules.filter(
        (r) => hasMeaningfulOutboundRule(r) || r.outboundId != null
      );
      const payload = opts.isCluster
        ? buildOutboundPropagationRulesClusterPayload(
            rulesToSubmit,
            opts.modeToSend!,
            opts.bgpNeighborIds.length > 0 ? opts.bgpNeighborIds : undefined,
            opts.ruleIdStr,
            ruleName
          )
        : buildOutboundPropagationRulesPayload(
            opts.bgpNeighborIds,
            rulesToSubmit,
            opts.isTelcoMode,
            opts.ruleIdStr,
            ruleName
          );
      return {
        ruleType: "outbound" as const,
        isUpdate: opts.isUpdate,
        payload,
        propagationRuleId: ruleIds.propagationRuleId || propagationRuleId,
      };
    },
    [ruleName, ruleIds.propagationRuleId, propagationRuleId]
  );
  
  // Fetch propagation rule details when modal opens in edit mode (by rule ID only).
  // Same getPropagationRuleById for both cluster and standalone; response bgpNeighbors are mapped to UI.
  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const fetchRuleDetails = async () => {
      if (!isOpen || mode !== "edit" || !getAccessTokenSilently || !tenantId || !propagationRuleId) {
        return;
      }
      if (initialValues?.inbound || initialValues?.outbound) {
        return;
      }

      setIsLoadingRuleDetails(true);
      setFetchedRuleDetails(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await getPropagationRuleById(token, propagationRuleId, tenantId);
        if (abortController.signal.aborted || !isMounted) return;

        const apiData = response.data?.data;
        if (!apiData) {
          setIsLoadingRuleDetails(false);
          return;
        }

        const mockRow = {
          bgpNeighborId: bgpNeighborId,
          bgpNeighborIp: neighborAddress,
          mode: tag === "Telco" ? "Telco" : "Enterprise",
          reflectorInstanceId: reflectorId,
        };

        // Pass the single rule object so new-format mapping runs (inbounds, outbounds, bgpNeighbors)
        const { result, ids } = transformRuleDetailsResponse(apiData, mockRow);

        // Map BGP neighbors from API so edit UI shows selected neighbors (cluster and standalone).
        // API may send id (standalone) or ids[] (cluster); use first id so each neighbor gets one chip.
        const allBgpNeighbors: Array<{ id: number; neighborAddress: string; peerAs: number }> = [];
        if (apiData.bgpNeighbors && Array.isArray(apiData.bgpNeighbors)) {
          apiData.bgpNeighbors.forEach((neighbor: any) => {
            const id = Array.isArray(neighbor.ids) && neighbor.ids.length > 0
              ? neighbor.ids[0]
              : (neighbor.id ?? undefined);
            const address = neighbor.neighborAddress ?? neighbor.address ?? '';
            if (id != null && !allBgpNeighbors.some(n => n.id === id)) {
              allBgpNeighbors.push({
                id: Number(id),
                neighborAddress: address,
                peerAs: neighbor.peerAs
              });
            }
          });
        }
        result._bgpNeighbors = allBgpNeighbors;
        result._bgpNeighborId = bgpNeighborId;
        result._mode = (apiData.mode === 'TELCO' || apiData.mode === 'Telco') ? 'Telco' : 'Enterprise';

        if (!abortController.signal.aborted && isMounted) {
          setFetchedRuleDetails(result);
          setRuleIds(ids);
        }
      } catch (error) {
        if (!abortController.signal.aborted && isMounted) {
          console.error('Error fetching propagation rule details:', error);
          setFetchedRuleDetails(null);
        }
      } finally {
        if (isMounted) setIsLoadingRuleDetails(false);
      }
    };

    fetchRuleDetails();
    return () => {
      abortController.abort();
      isMounted = false;
    };
  }, [isOpen, mode, propagationRuleId, getAccessTokenSilently, tenantId, clusterId, reflectorId, bgpNeighborId, neighborAddress, tag]);
  
  // Fetch BGP neighbors when modal opens
  useEffect(() => {
    const fetchBgpNeighbors = async () => {
      if (!isOpen || !getAccessTokenSilently) {
        return;
      }
      
      // Require either clusterId or reflectorId (instanceId)
      if (!clusterId && !reflectorId) {
        return;
      }
      
      setIsLoadingBgpNeighbors(true);
      try {
        const token = await getAccessTokenSilently();
        
        // Determine mode to pass to API: use selectedMode in add mode, tag prop in edit mode
        // Convert to uppercase for API (backend expects "ENTERPRISE" or "TELCO")
        const modeToFetch = mode === "add" 
          ? (selectedMode === "Telco" ? "TELCO" : "ENTERPRISE")
          : (tag === "Telco" ? "TELCO" : "ENTERPRISE");
        
        const response = await getBgpNeighborsDropdown(token, clusterId, reflectorId, modeToFetch);
        const neighbors = response.data.data || [];
        
        // Transform neighbors to dropdown options
        // Format label as "neighborAddress (ASN peerAs)"
        const options = neighbors.map((neighbor: any) => {
          const peerAs = neighbor.peerAs != null ? ` (ASN ${neighbor.peerAs})` : '';
          const label = `${neighbor.neighborAddress || 'Unknown'}${peerAs}`;
          
          return {
            value: neighbor.id?.toString() || '',
            label: label,
            // Store additional data for later use
            neighborAddress: neighbor.neighborAddress,
            peerAs: neighbor.peerAs,
            id: neighbor.id
          };
        });
        
        setBgpNeighborOptions(options);
      } catch (error) {
        console.error('Error fetching BGP neighbors:', error);
        setBgpNeighborOptions([]);
      } finally {
        setIsLoadingBgpNeighbors(false);
      }
    };
    
    fetchBgpNeighbors();
  }, [isOpen, getAccessTokenSilently, clusterId, reflectorId, mode, selectedMode, tag]);

  // Add BGP neighbor automatically when user selects from dropdown. Clear input after so user isn't confused.
  const addBgpNeighborByValue = (value: string) => {
    if (!value) return;
    const option = bgpNeighborOptions.find(opt => opt.value === value);
    if (!option || !option.id) return;
    const neighborId = option.id;
    setSelectedBgpNeighbors((prev) => {
      if (prev.some(n => n.id === neighborId)) return prev;
      const address = option.neighborAddress || option.label.split(' ')[0] || '';
      return [
        ...prev,
        { id: neighborId, address, label: option.label }
      ];
    });
    setSelectedBgpNeighbor(""); // Always clear so dropdown resets to "Select..."
  };

  // Remove BGP neighbor function
  const removeBgpNeighbor = (address: string) => {
    setSelectedBgpNeighbors(selectedBgpNeighbors.filter(n => n.address !== address));
  };

  // Update form state when rule details are loaded (fetch or initialValues).
  // Hydrate once per rule key — never re-apply stale fetchedRuleDetails during save/parent re-renders.
  React.useEffect(() => {
    if (!isOpen) {
      // Reset tracking when modal closes
      currentRuleKeyRef.current = null;
      formHydratedForRuleKeyRef.current = null;
      setSelectedBgpNeighbors([]);
      setSelectedBgpNeighbor("");
      return;
    }

    // Parent sets isSubmitting on save; re-hydrating here would flash pre-edit outbound/inbound state.
    if (isSubmittingLocal || isSubmitting) {
      return;
    }
    
    // Get the key for the rule we're about to edit
    const newRuleKey = getCurrentRuleKeyMemoized();
    
    // If we're switching to a different rule, reset state immediately
    if (currentRuleKeyRef.current !== null && currentRuleKeyRef.current !== newRuleKey) {
      setInbound(getDefaultInboundState());
      setOutbound({ ...defaultOutboundTabState });
      currentRuleKeyRef.current = newRuleKey;
      formHydratedForRuleKeyRef.current = null;
      setPendingInboundDeleteIds([]);
      setPendingOutboundDeleteIds([]);
    }

    // Already hydrated for this rule — user edits must win over stale fetch snapshot
    if (formHydratedForRuleKeyRef.current === newRuleKey) {
      return;
    }
    
    // Use fetchedRuleDetails if available (from API call), otherwise use initialValues prop
    const hasInitialValues =
      initialValues != null && typeof initialValues === "object" && Object.keys(initialValues).length > 0;
    const valuesToUse = fetchedRuleDetails || (hasInitialValues ? initialValues : null);

    // Edit mode: wait for GET-by-id before applying defaults (parent passes empty initialValues)
    if (
      !valuesToUse &&
      mode === "edit" &&
      propagationRuleId != null &&
      !hasInitialValues
    ) {
      return;
    }
    
    if (valuesToUse && Object.keys(valuesToUse).length > 0) {
      // Check if valuesToUse has meaningful data.
      // This must be consistent with our wizard gating checks, otherwise we may incorrectly
      // reset to defaults and lose prefill for previously configured rules.
      const hasInboundData = (() => {
        const inb = valuesToUse.inbound;
        if (!inb) {
          return false;
        }

        if (inb.vrfs && Array.isArray(inb.vrfs) && inb.vrfs.length > 0) {
          // In edit mode, if we have VRFs structure, consider it as having data
          // This ensures we don't reset when we have VRF data even if some fields are empty
          // For Telco mode, having VRFs means we have inbound configuration
          const hasData = inb.vrfs.some((vrf: any) => {
            const hasRoutingTables = Array.isArray(vrf.routingTables) && vrf.routingTables.length > 0;
            const hasEnis = Array.isArray(vrf.enis) && vrf.enis.length > 0;
            const hasRouteTargets = Array.isArray(vrf.routeTargets) && vrf.routeTargets.length > 0;
            const hasDynamicRouting = !!(vrf.routingTableTagName || vrf.routingTableTagValue);
            const hasDynamicNextHop = !!(vrf.nextHopTagName || vrf.nextHopTagValue);
            const hasVpc = !!(vrf.selectedVpc && String(vrf.selectedVpc).trim());
            const hasInboundId = !!(vrf.inboundId); // If it has an inboundId, it's a saved VRF
            // Consider it as having data if it has any of these OR if it has an inboundId (saved VRF)
            return hasRoutingTables || hasEnis || hasRouteTargets || hasDynamicRouting || hasDynamicNextHop || hasVpc || hasInboundId;
          });
          // If we have VRFs but the check failed, still return true in edit mode
          // This ensures we preserve the VRF structure even if some fields are empty
          if (mode === "edit" && !hasData && inb.vrfs.length > 0) {
            return true;
          }
          return hasData;
        }

        const hasRoutingTables = Array.isArray(inb.routingTables) && inb.routingTables.length > 0;
        const hasEnis = Array.isArray(inb.enis) && inb.enis.length > 0;
        const hasRouteTargets = Array.isArray(inb.routeTargets) && inb.routeTargets.length > 0;
        const hasDynamicRouting = !!(inb.routingTableTagName || inb.routingTableTagValue);
        const hasDynamicNextHop = !!(inb.nextHopTagName || inb.nextHopTagValue);
        const hasVpc = !!(inb.selectedVpc && String(inb.selectedVpc).trim());
        return hasRoutingTables || hasEnis || hasRouteTargets || hasDynamicRouting || hasDynamicNextHop || hasVpc;
      })();

      const hasOutboundData = (() => {
        return hasMeaningfulOutboundFormDataHelper(valuesToUse.outbound);
      })();
      
      // Use setTimeout to ensure this runs after any reset in the isOpen effect
      const timer = setTimeout(() => {
        if (isSubmittingLocal || isSubmitting) {
          return;
        }
        // In edit mode, use initialValues if available
        if (mode === "edit" && valuesToUse.inbound) {
          setInbound(valuesToUse.inbound);
        } else if (hasInboundData && valuesToUse.inbound) {
          setInbound(valuesToUse.inbound);
        } else if (mode === "edit" && valuesToUse.inbound) {
          // Edit mode: even if hasInboundData is false, if we have inbound structure, use it
          setInbound(valuesToUse.inbound);
        } else {
          // Only reset to defaults if we're NOT in edit mode
          if (mode !== "edit") {
            setInbound(getDefaultInboundState());
          }
        }
        
        // Handle outbound data
        if (mode === "edit" && valuesToUse.outbound) {
          setOutbound(normalizeOutboundFormData(valuesToUse.outbound));
        } else if (hasOutboundData && valuesToUse.outbound) {
          setOutbound(normalizeOutboundFormData(valuesToUse.outbound));
        } else if (mode === "edit" && valuesToUse.outbound) {
          setOutbound(normalizeOutboundFormData(valuesToUse.outbound));
        } else {
          if (mode !== "edit") {
            setOutbound({ ...defaultOutboundTabState });
          }
        }

        setRuleName(valuesToUse.name ?? "");
        
        // Update the current rule key after setting state
        currentRuleKeyRef.current = newRuleKey;
        formHydratedForRuleKeyRef.current = newRuleKey;
      }, 50);
      
      return () => clearTimeout(timer);
    } else {
      // Add mode (or edit without prefetch): apply defaults once per rule key
      setInbound(getDefaultInboundState());
      setOutbound({ ...defaultOutboundTabState });
      setRuleName("");
      currentRuleKeyRef.current = newRuleKey;
      formHydratedForRuleKeyRef.current = newRuleKey;
    }
  }, [fetchedRuleDetails, isOpen, mode, getDefaultInboundState, getCurrentRuleKeyMemoized, tag, isSubmitting, isSubmittingLocal, propagationRuleId]);

  // Separate effect to prefill BGP neighbors when rule details are loaded (edit mode)
  React.useEffect(() => {
    if (!isOpen) {
      return;
    }
    
    // Only prefill in edit mode
    if (mode !== "edit") {
      // In add mode, reset to empty
      if (selectedBgpNeighbors.length > 0) {
        setSelectedBgpNeighbors([]);
      }
      return;
    }
    
    // Prefill BGP neighbors from fetched rule details or initialValues
    const source = fetchedRuleDetails || initialValues;
    const bgpNeighbors = source?._bgpNeighbors;
    
    if (bgpNeighbors && Array.isArray(bgpNeighbors) && bgpNeighbors.length > 0) {
      const neighborsToSet = bgpNeighbors.map((neighbor: any) => {
        const peerAs = neighbor.peerAs != null ? ` (ASN ${neighbor.peerAs})` : '';
        const label = `${neighbor.neighborAddress || 'Unknown'}${peerAs}`;
        return {
          id: neighbor.id,
          address: neighbor.neighborAddress || '',
          label: label
        };
      });
      
      // Always update in edit mode if we have neighbors (don't check if different)
      // Use setTimeout to ensure this runs after any other state updates
      setTimeout(() => {
        setSelectedBgpNeighbors(neighborsToSet);
      }, 0);
    }
  }, [isOpen, mode, initialValues, fetchedRuleDetails]); // fetchedRuleDetails: prefill BGP neighbors when fetch completes


  // Handle mode change - reset inbound form when switching between Enterprise and Telco.
  // Mode (Enterprise/Telco) only governs inbound shape (flat vs. VRF list) — outbound
  // rules are independent of it, so switching modes must not touch outbound state
  // (previously wiped any outbound rules the user had already entered; see CTX-7).
  const handleModeChange = (newMode: "Enterprise" | "Telco") => {
    if (newMode === selectedMode) {
      return; // No change needed
    }

    // Update mode first
    setSelectedMode(newMode);
    // Reset inbound form state when switching modes - pass the new mode directly to get correct state
    setInbound(getDefaultInboundState(newMode));
    setActiveTab("inbound"); // Reset to inbound tab
  };
  
  // Effect to ensure form updates when selectedMode changes (in add mode)
  React.useEffect(() => {
    if (mode === "add" && isOpen) {
      // When mode changes, ensure form reflects the current mode
      const currentIsTelco = selectedMode === "Telco";
      const shouldHaveVrfs = currentIsTelco && (!inbound.vrfs || inbound.vrfs.length === 0);
      const shouldNotHaveVrfs = !currentIsTelco && inbound.vrfs && inbound.vrfs.length > 0;
      
      if (shouldHaveVrfs || shouldNotHaveVrfs) {
        setInbound(getDefaultInboundState());
      }
    }
  }, [selectedMode, mode, isOpen, getDefaultInboundState, inbound.vrfs]);
  
  // Cleanup function to reset form state when modal is closed
  const handleClose = () => {
    // Reset form state to defaults when closing modal
    setInbound(getDefaultInboundState());
    setOutbound({ ...defaultOutboundTabState });
    setRuleName("");
    setActiveTab("inbound"); // Reset to inbound tab
    setSelectedMode("Enterprise"); // Reset mode to default
    onClose();
  };

  // Handle modal open/close state changes - always reset on open to prevent stale data
  // The initialValues are handled by the separate useEffect above which will apply them after reset
  // Use a ref to track if we've already initialized to prevent resetting when getDefaultInboundState changes
  const hasInitializedRef = React.useRef(false);
  
  React.useEffect(() => {
    if (isOpen) {
      // Modal is opening - ALWAYS reset to defaults first to prevent carrying over stale data
      // Only reset on initial open, not when dependencies change
      if (!hasInitializedRef.current) {
        // The initialValues effect will then apply the correct data for the new rule
        setInbound(getDefaultInboundState());
        setOutbound({ ...defaultOutboundTabState });
        setRuleName("");
        setActiveTab("inbound"); // Reset to inbound tab
        // Reset mode to Enterprise in add mode
        if (mode === "add") {
          setSelectedMode("Enterprise");
        }
        hasInitializedRef.current = true;
      }
    } else {
      // Modal is closing - reset to defaults
      setInbound(getDefaultInboundState());
      setOutbound({ ...defaultOutboundTabState });
      setRuleName("");
      setActiveTab("inbound");
      setSelectedMode("Enterprise"); // Reset mode to default
      currentRuleKeyRef.current = null; // Clear the rule key when closing
      formHydratedForRuleKeyRef.current = null;
      hasInitializedRef.current = false; // Reset flag when modal closes
      setRuleIds({});
      setInboundFullyDeleted(false);
      setOutboundFullyDeleted(false);
      setPendingInboundDeleteIds([]);
      setPendingOutboundDeleteIds([]);
    }
  }, [isOpen, mode]); // Removed getDefaultInboundState from dependencies to prevent reset loop

  // Actually deletes the VRFs/outbound rules the user removed inside the modal
  // during this session. Called from handleSave right after Save is pressed —
  // never on Cancel/close — so a delete only reaches the backend once the
  // user confirms the whole form (CTX-154).
  const flushPendingDeletes = async (token: string) => {
    if (!tenantId) return;
    const ruleIdForDelete = ruleIds.propagationRuleId ?? propagationRuleId;
    if (ruleIdForDelete == null) return;

    for (const inboundIdToDelete of pendingInboundDeleteIds) {
      if (clusterId) {
        await deleteInboundPropagationRuleCluster(token, ruleIdForDelete, inboundIdToDelete, tenantId);
      } else {
        await deleteInboundPropagationRule(token, ruleIdForDelete, inboundIdToDelete, tenantId);
      }
    }
    for (const outboundIdToDelete of pendingOutboundDeleteIds) {
      if (clusterId) {
        await deleteOutboundPropagationRuleCluster(token, ruleIdForDelete, outboundIdToDelete, tenantId);
      } else {
        await deleteOutboundPropagationRule(token, ruleIdForDelete, outboundIdToDelete, tenantId);
      }
    }
    if (pendingInboundDeleteIds.length > 0) setPendingInboundDeleteIds([]);
    if (pendingOutboundDeleteIds.length > 0) setPendingOutboundDeleteIds([]);
  };

  const handleSave = async () => {
    // Set submitting state to block save button
    setIsSubmittingLocal(true);

    try {
      // Build payloads from current form state if meaningful, otherwise fall back to initialValues
      const inboundFormValues = hasMeaningfulInboundFormData() ? inbound : (initialValues?.inbound || inbound);
      // Below NEXTHOP_RETRY_MIN_VERSION the Retry block is inert; drop the fields
      // so an unsupported reflector never receives a setting it can't honour —
      // including when the rule was configured against a newer one (CTX-181).
      // Below NEXTHOP_WATCH_MIN_VERSION the same applies to the verification
      // fields: a retry-era reflector must never receive the -1 sentinel (CTX-347).
      let inboundValuesToSubmit = disableNexthopRetry
        ? withoutInboundNexthopRetry(inboundFormValues, isTelco)
        : inboundFormValues;
      // Dropped on the same condition that hides the block in the form: a rule
      // with no Dynamic ENI Resolver next-hop has nothing to verify, so it
      // sends nothing and whatever the backend already stores stays untouched.
      if (
        !useNexthopVerification ||
        !hasDynamicEniResolverNextHop(inboundValuesToSubmit, isTelco)
      ) {
        inboundValuesToSubmit = withoutNexthopVerification(inboundValuesToSubmit);
      }
      const outboundValuesToSubmit = hasMeaningfulOutboundFormData() ? outbound : (initialValues?.outbound || outbound);
      // Allow saving with only one side configured.
      // Only submit inbound if there's meaningful inbound data OR existing inbound data (not just any existing config)
      // Only submit outbound if there's meaningful outbound data OR existing outbound data (not just any existing config)
      // This prevents submitting inbound rules when user is only configuring outbound rules in telco cluster mode
      const shouldSubmitInbound = hasMeaningfulInboundFormData() || hasExistingInboundData;
      const shouldSubmitOutbound = hasMeaningfulOutboundFormData() || hasExistingOutboundData;

      setValidationError("");
      if (shouldSubmitInbound) {
        const inboundErr =
          validateInboundDynamicTags(inboundValuesToSubmit, isTelco) ??
          validateInboundNexthopRetry(inboundValuesToSubmit, isTelco) ??
          validateNexthopVerificationValues(inboundValuesToSubmit);
        if (inboundErr) {
          setValidationError(inboundErr);
          setIsSubmittingLocal(false);
          return;
        }
      }
      if (shouldSubmitOutbound) {
        const outboundErr = validateOutboundRules(
          normalizeOutboundFormData(outboundValuesToSubmit).rules
        );
        if (outboundErr) {
          setValidationError(outboundErr);
          setIsSubmittingLocal(false);
          return;
        }
      }

      // If clusterId is provided, handle cluster mode
      if (clusterId && getAccessTokenSilently) {
        const token = await getAccessTokenSilently();
        await flushPendingDeletes(token);

        // Build payloads - use bgpNeighborIds array (same format as standalone)
        const bgpNeighborIdsForCluster = getBgpNeighborIdsMemoized();
        
        // Determine mode: use selectedMode in add mode, tag prop in edit mode
        const modeToSend = mode === "add" 
          ? (selectedMode === "Telco" ? "TELCO" : "ENTERPRISE")
          : (tag === "Telco" ? "TELCO" : "ENTERPRISE");
        
        const inboundShouldUpdate = hasExistingInboundData;
        const outboundShouldUpdate = hasExistingOutboundData;
        const ruleIdStr = (ruleIds.propagationRuleId ?? propagationRuleId) != null ? String(ruleIds.propagationRuleId ?? propagationRuleId) : undefined;
        
        const inboundPayload = shouldSubmitInbound 
          ? buildInboundPropagationRuleClusterPayload({ ...inboundValuesToSubmit, name: ruleName }, modeToSend, bgpNeighborIdsForCluster.length > 0 ? bgpNeighborIdsForCluster : undefined, ruleIdStr ?? undefined)
          : null;

        const outboundSubmitOp = shouldSubmitOutbound
          ? buildOutboundSubmitOp(outboundValuesToSubmit, {
              isCluster: true,
              isTelcoMode: modeToSend === "TELCO",
              modeToSend,
              bgpNeighborIds: bgpNeighborIdsForCluster,
              ruleIdStr: ruleIdStr ?? undefined,
              isUpdate: outboundShouldUpdate,
            })
          : null;

        if (onSubmit) {
          await onSubmit({
            isClusterMode: true,
            batchOps: [
              ...(shouldSubmitInbound && inboundPayload ? [{ 
                ruleType: 'inbound', 
                isUpdate: inboundShouldUpdate, 
                payload: inboundPayload,
                propagationRuleId: ruleIds.propagationRuleId || propagationRuleId,
                inboundId: ruleIds.inboundId || inboundId
              }] : []),
              ...(outboundSubmitOp ? [outboundSubmitOp] : []),
            ],
          });
          handleClose();
          setIsSubmittingLocal(false);
          return;
        }

        // Fallback (no onSubmit): sequential create — inbound first to get UUID, then outbound with that UUID; or only one direction.
        if (!tenantId) {
          setIsSubmittingLocal(false);
          return;
        }
        let propagationRuleIdFromResponse: string | undefined;
        if (shouldSubmitInbound && inboundPayload) {
          if (inboundShouldUpdate && ruleIdStr) {
            await updateInboundPropagationRuleCluster(token, clusterId, ruleIdStr, inboundPayload, tenantId);
            propagationRuleIdFromResponse = ruleIdStr;
          } else {
            const res = await createInboundPropagationRuleCluster(token, clusterId, inboundPayload, tenantId);
            const uuid = res?.data?.data?.propagationRuleId;
            propagationRuleIdFromResponse = uuid != null ? String(uuid) : undefined;
          }
        }
        if (shouldSubmitOutbound && outboundSubmitOp) {
          const outboundRuleId = propagationRuleIdFromResponse ?? ruleIdStr;
          const payloadWithRuleId = outboundRuleId
            ? { ...outboundSubmitOp.payload, propagationRuleId: outboundRuleId }
            : outboundSubmitOp.payload;
          if (outboundSubmitOp.isUpdate && outboundRuleId) {
            await updateOutboundPropagationRulesCluster(token, clusterId, outboundRuleId, payloadWithRuleId, tenantId);
          } else {
            await createOutboundPropagationRuleCluster(token, clusterId, payloadWithRuleId, tenantId);
          }
        }
        handleClose();
        setIsSubmittingLocal(false);
        return;
      }
      
      // Standalone mode: Handle create vs update logic
      if (getAccessTokenSilently) {
        // Get BGP neighbor IDs using helper function
        const bgpNeighborIds = getBgpNeighborIdsMemoized();

        // Validate that we have at least one BGP neighbor ID
        if (bgpNeighborIds.length === 0) {
          console.error('No BGP neighbors selected. Please select at least one BGP neighbor.');
          setIsSubmittingLocal(false);
          return;
        }

        const token = await getAccessTokenSilently();
        await flushPendingDeletes(token);

        const inboundShouldUpdate = hasExistingInboundData;
        const outboundShouldUpdate = hasExistingOutboundData;
        const ruleIdStr = (ruleIds.propagationRuleId ?? propagationRuleId) != null ? String(ruleIds.propagationRuleId ?? propagationRuleId) : undefined;
        const inboundPayload = buildInboundPropagationRulePayload(bgpNeighborIds, { ...inboundValuesToSubmit, name: ruleName }, isTelco, ruleIdStr ?? undefined);
        const outboundSubmitOp = shouldSubmitOutbound
          ? buildOutboundSubmitOp(outboundValuesToSubmit, {
              isCluster: false,
              isTelcoMode: isTelco,
              bgpNeighborIds,
              ruleIdStr: ruleIdStr ?? undefined,
              isUpdate: outboundShouldUpdate,
            })
          : null;

        if (onSubmit) {
          await onSubmit({
            batchOps: [
              ...(shouldSubmitInbound ? [{ 
                ruleType: 'inbound', 
                isUpdate: inboundShouldUpdate, 
                payload: inboundPayload,
                propagationRuleId: ruleIds.propagationRuleId || propagationRuleId,
                inboundId: ruleIds.inboundId || inboundId
              }] : []),
              ...(outboundSubmitOp ? [outboundSubmitOp] : []),
            ],
          });
        } else {
          if (!tenantId) return;
          const firstBgpNeighborId = bgpNeighborIds[0];
          if (shouldSubmitInbound && firstBgpNeighborId) {
            await createInboundPropagationRule(token, firstBgpNeighborId, inboundPayload, tenantId);
          }
          if (shouldSubmitOutbound && outboundSubmitOp && firstBgpNeighborId) {
            if (outboundSubmitOp.isUpdate && ruleIdStr) {
              await updateOutboundPropagationRules(token, ruleIdStr, outboundSubmitOp.payload, tenantId);
            } else {
              await createOutboundPropagationRule(token, firstBgpNeighborId, outboundSubmitOp.payload, tenantId);
            }
          }
        }

        handleClose();
        setIsSubmittingLocal(false);
        return;
      }
      
      // Fallback to original onSubmit if no token or bgpNeighborId
      if (onSubmit) {
        const result = onSubmit({ inbound, outbound, activeTab });
        // Handle both sync and async onSubmit
        if (result instanceof Promise) {
          await result;
        }
      }
      handleClose();
      setIsSubmittingLocal(false);
    } catch (error) {
      console.error('Error handling propagation rule:', error);
      setValidationError(getSaveErrorMessage(error));
      setIsSubmittingLocal(false); // Reset on error
      // Don't close modal on error so user can retry
    }
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      hideScrollbar={true}
      title={
        <div className="flex items-center gap-2 space-x-1">
          {title}
          {badge}
          {tag && (
            <GenericTag
              label={tag}
              variant={tag === "Telco" ? "warning" : "neutral"}
            />
          )}
        </div>
      }
    >
      <div className="flex flex-col items-center p-6 pt-2 gap-4 min-h-[400px] text-body relative">
        {isLoadingRuleDetails ? (
          /* Skeleton while fetching rule details in edit mode */
          <div className="w-full flex flex-col gap-4" aria-label="Loading rule details">
            <div className="w-full">
              <SkeletonLine w={80} h={14} className="mb-2" />
              <SkeletonBlock h={40} w="100%" className="rounded-md" />
              <SkeletonLine w="70%" h={12} className="mt-2" />
            </div>
            <div className="w-full">
              <SkeletonLine w={100} h={14} className="mb-2" />
              <div className="flex gap-2">
                <SkeletonBlock h={40} className="flex-1 rounded-md" />
                <SkeletonBlock h={40} w={40} className="rounded-md shrink-0" />
              </div>
            </div>
            <div className="w-full flex gap-2">
              <SkeletonBlock h={36} w={80} className="rounded-md" />
              <SkeletonBlock h={36} w={80} className="rounded-md" />
            </div>
            <div className="w-full space-y-3">
              <SkeletonLine w={120} h={14} className="mb-2" />
              <SkeletonBlock h={48} w="100%" className="rounded-md" />
              <SkeletonBlock h={48} w="100%" className="rounded-md" />
              <SkeletonBlock h={48} w="80%" className="rounded-md" />
            </div>
            <div className="flex justify-end gap-4 mt-6 w-full">
              <SkeletonBlock h={40} w={90} className="rounded-md" />
              <SkeletonBlock h={40} w={70} className="rounded-md" />
            </div>
          </div>
        ) : (
          <>
            {/* Name (outside tabs, visible for both inbound and outbound) */}
            <div className="w-full mb-4">
              <GenericInput
                label="Name"
                placeholder="Enter rule name"
                value={ruleName}
                onChange={(v) => setRuleName(v)}
                type="text"
                fullWidth
              />
            </div>

            {/* Mode Selection */}
            <div className="w-full">
              <label className="block text-sm font-medium text-semantic-text-primary mb-2">
                Mode <span className="text-semantic-text-brand">*</span>
              </label>
              <div className="flex items-center gap-2 w-full mb-2">
                {mode === "add" ? (
                  <GenericDropdown
                    key={`mode-selector-${selectedMode}`}
                    items={["Enterprise", "Telco"]}
                    value={selectedMode}
                    defaultLabel="Select mode..."
                    onSelect={(v) => {
                      handleModeChange(v as "Enterprise" | "Telco");
                    }}
                    widthClass="w-full"
                    className="w-full flex-1"
                    minWidthClass=""
                    maxWidthClass=""
                    size="md"
                  />
                ) : (
                  <GenericDropdown
                    key={`mode-selector-edit-${tag}`}
                    items={["Enterprise", "Telco"]}
                    value={tag || "Enterprise"}
                    defaultLabel="Select mode..."
                    onSelect={() => {
                      // Disabled in edit mode
                    }}
                    widthClass="w-full"
                    className="w-full flex-1"
                    minWidthClass=""
                    maxWidthClass=""
                    size="md"
                    disabled={true}
                  />
                )}
              </div>
              
              <div className="text-xs text-semantic-text-secondary mt-1">
                {mode === "add" 
                  ? "Select the mode for this propagation rule. Telco mode includes VRF configuration."
                  : `Mode cannot be changed in edit mode. Current mode: ${tag || "Enterprise"}`
                }
              </div>
            </div>
            
            {/* BGP Neighbors Select */}
            <div className="w-full">
              <label className="block text-sm font-medium text-semantic-text-primary mb-2">
                BGP Neighbors
              </label>
              <div className="w-full mb-2">
                <GenericSearchableDropdown
                  items={(bgpNeighborOptions || []).filter(opt => !selectedBgpNeighbors.some(n => n.id === opt.id))}
                  value={selectedBgpNeighbor}
                  onSelect={(v) => addBgpNeighborByValue(v)}
                  defaultLabel="Select..."
                  widthClass="w-full"
                  className="w-full"
                  minWidthClass=""
                  maxWidthClass=""
                  disabled={isLoadingBgpNeighbors}
                  emptyItemsMessage={isLoadingBgpNeighbors ? "Loading neighbors..." : "No BGP neighbors available"}
                  allowCustomValues={false}
                />
              </div>
              {selectedBgpNeighbors.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedBgpNeighbors.map((neighbor, index) => (
                    <div key={`${neighbor.address}-${index}`} className="flex items-center">
                      {/* @ts-ignore - removable prop exists in GenericTag but TypeScript type inference issue */}
                      <GenericTag
                        label={neighbor.label}
                        variant="default"
                        removable
                        onRemove={() => removeBgpNeighbor(neighbor.address)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="w-full flex justify-between items-center">
              <GenericTabs
                tabs={TABS}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            </div>
            <div className="w-full">
              <PropagationTabContent
                values={activeTab === "inbound" ? inbound : outbound}
                setValues={activeTab === "inbound" ? setInbound : setOutbound}
                isTelco={isTelco}
                direction={activeTab as "inbound" | "outbound"}
                reflectorId={reflectorId}
                clusterId={clusterId}
                getAccessTokenSilently={getAccessTokenSilently}
                bgpNeighborId={bgpNeighborId}
                propagationRuleId={propagationRuleId}
                disableDynamicTagOptions={disableDynamicTagOptions}
                disableNexthopRetry={disableNexthopRetry}
                useNexthopVerification={useNexthopVerification}
                onVrfDeleted={(remainingVrfCount: number, deletedInboundId?: number) => {
                  // Queue the real DELETE for when the user presses "Save" —
                  // see the pendingInboundDeleteIds loop in handleSave (CTX-154).
                  if (deletedInboundId != null) {
                    setPendingInboundDeleteIds((prev) =>
                      prev.includes(deletedInboundId) ? prev : [...prev, deletedInboundId]
                    );
                  }
                  // Once the last VRF is gone, this rule no longer has a
                  // persisted inbound — clear the stale id and force
                  // hasExistingInboundData to false so a subsequent Save
                  // doesn't try to "update" an inbound that no longer
                  // exists (which corrupts the Telco payload; see CTX-7).
                  if (remainingVrfCount === 0) {
                    setRuleIds((prev) => ({ ...prev, inboundId: undefined }));
                    setInboundFullyDeleted(true);
                  }
                }}
                onOutboundRuleDeleted={(remainingRuleCount: number, deletedOutboundId?: number) => {
                  if (deletedOutboundId != null) {
                    setPendingOutboundDeleteIds((prev) =>
                      prev.includes(deletedOutboundId) ? prev : [...prev, deletedOutboundId]
                    );
                  }
                  if (remainingRuleCount === 0) {
                    setRuleIds((prev) => ({ ...prev, outboundId: undefined }));
                    setOutboundFullyDeleted(true);
                  }
                }}
              />
            </div>
            {validationError && (
              <GenericInlineAlert variant="warning" className="mt-4">
                {validationError}
              </GenericInlineAlert>
            )}
            <div className="flex justify-end gap-4 mt-6 w-full">
              <GenericButton label="Cancel" variant="secondary" onClick={handleClose} disabled={isSubmitting || isSubmittingLocal || isLoadingRuleDetails} />
              <GenericButton
                label={(isSubmitting || isSubmittingLocal || isLoadingRuleDetails) ? (isLoadingRuleDetails ? "Loading..." : "Saving...") : "Save"}
                variant="brand"
                onClick={handleSave}
                disabled={
                  isSubmitting ||
                  isSubmittingLocal ||
                  isLoadingRuleDetails ||
                  (!bgpNeighborId && selectedBgpNeighbors.length === 0) ||
                  (!hasMeaningfulInboundFormData() &&
                   !hasMeaningfulOutboundFormData() &&
                   !hasExistingInboundData &&
                   !hasExistingOutboundData &&
                   !hasExistingConfig)
                }
                loading={isSubmitting || isSubmittingLocal || isLoadingRuleDetails}
              />
            </div>
          </>
        )}
      </div>
    </GenericModal>
  );
};

export default CPModal;
